import { supabase } from './supabase';

export type FriendStatus = 'pending' | 'accepted' | 'rejected' | 'blocked';

export const sendFriendRequest = async (receiverId: string) => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) throw authError;
    if (!user) throw new Error('You must be logged in to send friend requests');

    // Create the friend request
    const { data: newRequest, error: insertError } = await supabase
      .from('friends')
      .insert({
        sender_id: user.id,
        receiver_id: receiverId,
        status: 'pending'
      })
      .select(`
        *,
        sender:profiles!friends_sender_id_fkey(
          id, 
          full_name, 
          profile_picture
        ),
        receiver:profiles!friends_receiver_id_fkey(
          id, 
          full_name, 
          profile_picture
        )
      `)
      .single();

    if (insertError) throw insertError;
    return newRequest;
  } catch (err) {
    console.error('Error sending friend request:', err);
    throw err;
  }
};

export const getFriendStatus = async (userId: string) => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (!user) return null;

    // Check if there's an existing friend request
    const { data: request, error: requestError } = await supabase
      .from('friends')
      .select(`
        *,
        sender:profiles!friends_sender_id_fkey(
          id, 
          full_name, 
          profile_picture
        ),
        receiver:profiles!friends_receiver_id_fkey(
          id, 
          full_name, 
          profile_picture
        )
      `)
      .or(
        `and(sender_id.eq.${user.id},receiver_id.eq.${userId}),` +
        `and(sender_id.eq.${userId},receiver_id.eq.${user.id})`
      )
      .order('created_at', { ascending: false })
      .maybeSingle();

    if (requestError) throw requestError;
    
    // If there's an accepted request, return it
    if (request && request.status === 'accepted') {
      return request;
    }
    
    // Check if they're friends in the friends_accepted table
    const { data: areFriends, error: friendsError } = await supabase
      .from('friends_accepted')
      .select('*')
      .or(
        `and(user1_id.eq.${user.id},user2_id.eq.${userId}),` +
        `and(user1_id.eq.${userId},user2_id.eq.${user.id})`
      )
      .maybeSingle();
      
    if (friendsError) throw friendsError;
    
    if (areFriends) {
      // They're friends in the friends_accepted table
      return { ...request, status: 'accepted' as FriendStatus };
    }
    
    // Return the request (pending/rejected) or null
    return request;
  } catch (err) {
    console.error('Error getting friend status:', err);
    throw err;
  }
};

export const updateFriendRequest = async (requestId: string, status: FriendStatus) => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) throw authError;
    if (!user) throw new Error('You must be logged in to update friend requests');

    // Get the friend request first to ensure it exists
    const { data: request, error: requestError } = await supabase
      .from('friends')
      .select('*')
      .eq('id', requestId)
      .single();

    if (requestError) throw requestError;
    if (!request) throw new Error('Friend request not found');

    // Update the request status
    const { data: updatedRequest, error: updateError } = await supabase
      .from('friends')
      .update({ status })
      .eq('id', requestId)
      .select(`
        *,
        sender:profiles!friends_sender_id_fkey(
          id, 
          full_name, 
          profile_picture
        ),
        receiver:profiles!friends_receiver_id_fkey(
          id, 
          full_name, 
          profile_picture
        )
      `)
      .single();

    if (updateError) throw updateError;

    // If accepting the request, create the friendship record in friends_accepted
    if (status === 'accepted') {
      // Ensure user1_id is always the smaller UUID and user2_id is the larger UUID
      // This is to satisfy the check constraint (user1_id < user2_id)
      const user1Id = request.sender_id < request.receiver_id ? request.sender_id : request.receiver_id;
      const user2Id = request.sender_id > request.receiver_id ? request.sender_id : request.receiver_id;

      const { error: friendshipError } = await supabase
        .from('friends_accepted')
        .insert({
          user1_id: user1Id,
          user2_id: user2Id,
          friend_request_id: requestId
        });

      if (friendshipError) {
        console.error('Error creating friendship record:', friendshipError);
        // Don't throw here, as the status update was successful
      }
    }

    return updatedRequest;
  } catch (err) {
    console.error('Error updating friend request:', err);
    throw err;
  }
};

export const getFriendsList = async () => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) throw authError;
    if (!user) return [];

    // Get friends from friends_accepted table
    const { data: acceptedFriends, error: acceptedError } = await supabase
      .from('friends_accepted')
      .select(`
        id,
        user1:profiles!friends_accepted_user1_id_fkey(
          id, 
          full_name, 
          profile_picture
        ),
        user2:profiles!friends_accepted_user2_id_fkey(
          id, 
          full_name, 
          profile_picture
        )
      `)
      .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`);

    if (acceptedError) throw acceptedError;

    // Transform the data to return consistent friend objects
    return (acceptedFriends || []).map(friendship => {
      const friend = friendship.user1.id === user.id ? friendship.user2 : friendship.user1;
      return {
        id: friendship.id,
        friend: friend
      };
    });
  } catch (err) {
    console.error('Error getting friends list:', err);
    throw err;
  }
};

export const unfriend = async (friendshipId: string) => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) throw authError;
    if (!user) throw new Error('You must be logged in to unfriend users');

    // Delete the friendship record
    const { error: deleteError } = await supabase
      .from('friends_accepted')
      .delete()
      .eq('id', friendshipId);

    if (deleteError) throw deleteError;
  } catch (err) {
    console.error('Error unfriending user:', err);
    throw err;
  }
};